import React, { useRef, useState } from 'react';
import { FaCamera, FaXmark } from 'react-icons/fa6';

// Mobile-friendly photo capture/upload → compressed base64 data URL.
// Canvas downscale keeps DB rows small (D1 1MB limit) even with 30 photos a day.
const MAX_DIM = 900;
const JPEG_QUALITY = 0.72;

export function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read-fail'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('decode-fail'));
      img.onload = () => {
        const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function PhotoInput({ value, onChange, label = 'ছবি', hint, small }) {
  const [preview, setPreview] = useState(value || null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await compressImage(file);
      setPreview(dataUrl);
      onChange(dataUrl);
    } catch {
      alert('ছবিটি পড়া যাচ্ছে না। অন্য ছবি চেষ্টা করুন।');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  }

  return (
    <div>
      {!small && <label className="label flex items-center gap-1.5">{label}{hint && <span className="font-normal text-ink-300">({hint})</span>}</label>}
      {preview ? (
        <div className="relative">
          <img src={preview} alt="preview" className={`${small ? 'w-16 h-16 rounded-lg' : 'w-full h-40 sm:h-44'} object-cover border border-ink-200`} />
          <button type="button" onClick={() => { setPreview(null); onChange(null); }}
            className="absolute -top-1.5 -right-1.5 p-1 bg-ink-900/70 text-white rounded-full hover:bg-ink-900 transition">
            <FaXmark size={10} />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className={`${small ? 'w-16 h-14' : 'w-full h-28'} rounded-xl border-2 border-dashed border-ink-200 bg-ink-50 flex flex-col items-center justify-center text-ink-400 hover:border-brand-400 hover:text-brand-600 transition ${busy ? 'opacity-50' : ''}`}>
          <FaCamera className={`${small ? 'text-sm' : 'text-2xl mb-1'}`} />
          {!small && <span className="text-xs font-medium">{busy ? 'প্রসেস হচ্ছে…' : 'ছবি তুলুন / আপলোড করুন'}</span>}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" />
    </div>
  );
}